import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import Avatar from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowRight, Send, MessageSquare, Plus, User as UserIcon, GraduationCap, Users as UsersIcon, UserCheck, LogOut, Trash2, Check, CheckCheck } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const ROLE_LABEL = { student: "طالبة", parent: "ولي أمر", trainer: "مدربة" };
const ROLE_ICON = { student: GraduationCap, parent: UsersIcon, trainer: UserCheck };
const SPEC_LABEL = { quantitative: "كمي", verbal: "لفظي", both: "كمي/لفظي" };

export default function Messages() {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [conversations, setConversations] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [active, setActive] = useState(null);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState("");
    const [pickerOpen, setPickerOpen] = useState(false);
    const [contactQuery, setContactQuery] = useState("");
    const [delTarget, setDelTarget] = useState(null);
    const [peerTyping, setPeerTyping] = useState(false);
    const scrollRef = useRef(null);
    // Track "just-created" empty drafts so we can clean up if the user walks away
    const draftRef = useRef(null); // { id, everSent }
    const lastTypingSentAt = useRef(0);

    const loadConversations = async () => {
        try { const { data } = await api.get("/messages/conversations"); setConversations(data); }
        catch (e) { console.error(e); }
    };
    const loadContacts = async () => {
        try { const { data } = await api.get("/messages/contacts"); setContacts(data); }
        catch (e) { console.error(e); }
    };
    const loadMessages = async (convId) => {
        try {
            const { data } = await api.get(`/messages/conversations/${convId}/messages`);
            setMessages(data);
            // If the currently-tracked draft already has messages, promote it (it was resumed, not empty)
            if (draftRef.current && draftRef.current.id === convId && data.length > 0) {
                draftRef.current.everSent = true;
            }
            setTimeout(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, 80);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    /** If the user just opened a brand-new empty conversation and never sent anything, delete it. */
    const cleanupDraft = async () => {
        const d = draftRef.current;
        if (!d) return;
        draftRef.current = null;
        if (d.everSent) return;
        try { await api.delete(`/messages/conversations/${d.id}`); } catch { /* silent */ }
    };

    useEffect(() => { loadConversations(); loadContacts(); }, []);

    // Handle ?to=<user_id> query param: auto-open (or create) a conversation with that user
    useEffect(() => {
        const to = searchParams.get("to");
        if (!to || contacts.length === 0) return;
        const contact = contacts.find((c) => c.id === to);
        if (!contact) return;
        // Clear the param so we don't re-trigger
        searchParams.delete("to");
        setSearchParams(searchParams, { replace: true });
        startWithContact(contact);
        // eslint-disable-next-line
    }, [contacts, searchParams]);
    useEffect(() => {
        const iv = setInterval(() => {
            loadConversations();
            if (active) loadMessages(active.id);
        }, 5000);
        return () => clearInterval(iv);
        // eslint-disable-next-line
    }, [active?.id]);

    // Poll peer typing status (faster than messages, but lightweight)
    useEffect(() => {
        if (!active) { setPeerTyping(false); return; }
        let stopped = false;
        const poll = async () => {
            try {
                const { data } = await api.get(`/messages/conversations/${active.id}/typing`);
                if (!stopped) setPeerTyping((data.typing || []).length > 0);
            } catch { /* ignore */ }
        };
        poll();
        const iv = setInterval(poll, 2500);
        return () => { stopped = true; clearInterval(iv); setPeerTyping(false); };
    }, [active?.id]);

    const onTextChange = (v) => {
        setText(v);
        if (!active || !v.trim()) return;
        const now = Date.now();
        // Debounce typing pings to at most once per 3 seconds
        if (now - lastTypingSentAt.current < 3000) return;
        lastTypingSentAt.current = now;
        api.post(`/messages/conversations/${active.id}/typing`).catch(() => {});
    };

    // Beforeunload / unmount cleanup: delete empty draft the user abandoned
    useEffect(() => {
        const handler = () => {
            const d = draftRef.current;
            if (!d || d.everSent) return;
            // navigator.sendBeacon needed since fetch may be canceled
            try {
                const token = localStorage.getItem("bqns_token");
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/messages/conversations/${d.id}`;
                // sendBeacon does not support DELETE; fallback to fetch keepalive
                fetch(url, { method: "DELETE", keepalive: true, headers: token ? { Authorization: `Bearer ${token}` } : {} });
            } catch {}
        };
        window.addEventListener("beforeunload", handler);
        return () => {
            window.removeEventListener("beforeunload", handler);
            cleanupDraft();
        };
    }, []);

    const openConversation = async (conv) => {
        // switching conversations: cleanup any abandoned draft first
        await cleanupDraft();
        setActive(conv);
        await loadMessages(conv.id);
    };

    const startWithContact = async (contact) => {
        try {
            // cleanup previously abandoned draft (if any) before starting a new one
            await cleanupDraft();
            const { data } = await api.post("/messages/conversations", { peer_user_id: contact.id });
            setPickerOpen(false);
            // Check if this conv already has messages; if it does, it's a resumed chat, not a draft
            let hasMessages = false;
            try {
                const r = await api.get(`/messages/conversations/${data.id}/messages`);
                hasMessages = (r.data || []).length > 0;
                setMessages(r.data || []);
            } catch { setMessages([]); }
            draftRef.current = { id: data.id, everSent: hasMessages };
            setActive({ id: data.id, peer: contact, last_message_at: null, last_message_preview: null, unread: 0 });
            await loadConversations();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const send = async () => {
        if (!text.trim() || !active) return;
        const content = text.trim();
        setText("");
        try {
            await api.post(`/messages/conversations/${active.id}/messages`, { content });
            // Promote draft: it is no longer empty
            if (draftRef.current && draftRef.current.id === active.id) {
                draftRef.current.everSent = true;
            }
            await loadMessages(active.id);
            await loadConversations();
        } catch (e) { toast.error(formatApiError(e)); setText(content); }
    };

    const deleteMessage = async () => {
        if (!delTarget) return;
        try {
            await api.delete(`/messages/${delTarget.id}`);
            toast.success("تم حذف الرسالة من محادثتك");
            setDelTarget(null);
            await loadMessages(active.id);
            await loadConversations();
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const back = async () => {
        await cleanupDraft();
        const path = user?.role === "student" ? "/student" : user?.role === "parent" ? "/parent" : "/trainer";
        nav(path);
    };

    const filtered = contacts.filter((c) =>
        !contactQuery.trim() || (c.full_name || "").toLowerCase().includes(contactQuery.trim().toLowerCase())
    );

    return (
        <div dir="rtl" className="min-h-screen bg-slate-50 flex flex-col">
            <header className="bg-[#0F172A] text-white sticky top-0 z-10">
                <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="sm" onClick={back} className="text-white/80 hover:text-white hover:bg-white/10" data-testid="btn-back-messages">
                            <ArrowRight className="w-4 h-4 ml-1" /> رجوع
                        </Button>
                        <BrandLogo size={36} />
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="hidden sm:flex items-center gap-2 text-sm">
                            <MessageSquare className="w-4 h-4 text-[#C8102E]" />
                            <span>الرسائل</span>
                        </div>
                        <Button variant="ghost" size="sm" onClick={async () => { await logout(); nav("/login"); }} className="text-white/80 hover:text-white hover:bg-white/10">
                            <LogOut className="w-4 h-4 ml-1" /> خروج
                        </Button>
                    </div>
                </div>
            </header>

            <main className="flex-1 max-w-7xl w-full mx-auto p-4 md:p-6">
                <div className="grid md:grid-cols-[320px_1fr] gap-4 h-[calc(100vh-120px)]">
                    <div className="bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden">
                        <div className="p-3 border-b border-slate-100 flex items-center justify-between">
                            <div className="font-semibold text-[#0F172A]">المحادثات</div>
                            <Button size="sm" onClick={() => setPickerOpen(true)} data-testid="btn-new-chat" className="bg-[#1B2A4E] hover:bg-[#0F172A]">
                                <Plus className="w-4 h-4 ml-1" /> جديدة
                            </Button>
                        </div>
                        <div className="flex-1 overflow-y-auto">
                            {conversations.length === 0 && <div className="text-center text-slate-400 text-sm p-6">لا توجد محادثات — ابدئي واحدة جديدة</div>}
                            {conversations.map((c) => {
                                const Icon = ROLE_ICON[c.peer?.role] || UserIcon;
                                return (
                                    <button
                                        key={c.id}
                                        onClick={() => openConversation(c)}
                                        data-testid={`conv-${c.id}`}
                                        className={`w-full text-right p-3 border-b border-slate-100 hover:bg-slate-50 flex items-center gap-3 ${active?.id === c.id ? "bg-slate-100" : ""}`}
                                    >
                                        <Avatar url={c.peer?.avatar_url} name={c.peer?.full_name} size={40} />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium text-sm truncate">{c.peer?.full_name || "—"}</span>
                                                {c.unread > 0 && <Badge className="bg-[#C8102E] text-white text-[10px] h-5 min-w-5 flex items-center justify-center px-1.5">{c.unread}</Badge>}
                                            </div>
                                            <div className="text-xs text-slate-500 truncate mt-0.5">{c.last_message_preview || "لا رسائل بعد"}</div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden">
                        {!active && (
                            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-3">
                                <MessageSquare className="w-14 h-14 text-slate-200" />
                                <div className="text-sm">اختاري محادثة أو ابدئي محادثة جديدة</div>
                            </div>
                        )}
                        {active && (
                            <>
                                <div className="p-4 border-b border-slate-100 flex items-center gap-3">
                                    <Avatar url={active.peer?.avatar_url} name={active.peer?.full_name} size={40} />
                                    <div>
                                        <div className="font-semibold text-[#0F172A]">{active.peer?.full_name}</div>
                                        <div className="text-xs text-slate-500">{ROLE_LABEL[active.peer?.role] || "—"}</div>
                                    </div>
                                </div>
                                <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-slate-50">
                                    {messages.length === 0 && !peerTyping && <div className="text-center text-slate-400 text-sm py-8">ابدئي المحادثة بأول رسالة</div>}
                                    {messages.map((m) => {
                                        const mine = m.sender_id === user.id;
                                        return (
                                            <div key={m.id} className={`group flex ${mine ? "justify-start" : "justify-end"} items-center gap-1`}>
                                                {mine && (
                                                    <button
                                                        onClick={() => setDelTarget(m)}
                                                        data-testid={`btn-del-msg-${m.id}`}
                                                        title="حذف من محادثتي"
                                                        aria-label="حذف من محادثتي"
                                                        className="shrink-0 p-2 rounded-full text-slate-400 hover:text-[#C8102E] hover:bg-[#FFF1F2] transition-colors"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                                <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-[#1B2A4E] text-white" : "bg-white border border-slate-200 text-[#0F172A]"}`}>
                                                    <div className="whitespace-pre-wrap break-words">{m.content}</div>
                                                    <div className={`flex items-center gap-1 mt-1 ${mine ? "text-white/60 justify-start" : "text-slate-400 justify-end"} text-[10px]`} dir="ltr">
                                                        <span className="font-mono-num">
                                                            {new Date(m.created_at).toLocaleTimeString("ar-EG", { hour: "2-digit", minute: "2-digit" })}
                                                        </span>
                                                        {mine && (
                                                            (m.read_by || []).includes(active.peer?.id)
                                                                ? <CheckCheck className="w-3.5 h-3.5 text-emerald-300" data-testid={`msg-read-${m.id}`} aria-label="قُرِئت" />
                                                                : <Check className="w-3.5 h-3.5 text-white/60" data-testid={`msg-sent-${m.id}`} aria-label="مُرسلة" />
                                                        )}
                                                    </div>
                                                </div>
                                                {!mine && (
                                                    <button
                                                        onClick={() => setDelTarget(m)}
                                                        data-testid={`btn-del-msg-${m.id}`}
                                                        title="حذف من محادثتي"
                                                        aria-label="حذف من محادثتي"
                                                        className="shrink-0 p-2 rounded-full text-slate-400 hover:text-[#C8102E] hover:bg-[#FFF1F2] transition-colors"
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {peerTyping && (
                                        <div className="flex justify-end" data-testid="typing-indicator">
                                            <div className="bg-white border border-slate-200 rounded-2xl px-4 py-2.5 text-xs text-slate-500 inline-flex items-center gap-2">
                                                <span>{active.peer?.full_name?.split(" ")[0] || "الطرف الآخر"} تكتب الآن</span>
                                                <span className="inline-flex gap-1 items-center">
                                                    <span className="typing-dot" />
                                                    <span className="typing-dot" style={{ animationDelay: "0.15s" }} />
                                                    <span className="typing-dot" style={{ animationDelay: "0.3s" }} />
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="p-3 border-t border-slate-100 flex items-end gap-2">
                                    <Textarea
                                        value={text}
                                        onChange={(e) => onTextChange(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                                        data-testid="msg-input"
                                        placeholder="اكتبي رسالتك... (Shift+Enter لسطر جديد)"
                                        className="resize-none min-h-11 max-h-32"
                                        rows={1}
                                    />
                                    <Button onClick={send} data-testid="btn-send-msg" disabled={!text.trim()} className="bg-[#1B2A4E] hover:bg-[#0F172A] h-11 px-4">
                                        <Send className="w-4 h-4" />
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </main>

            <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
                <DialogContent dir="rtl" className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>ابدئي محادثة جديدة</DialogTitle>
                        <DialogDescription>اختاري جهة التواصل من القائمة أدناه</DialogDescription>
                    </DialogHeader>
                    <Input value={contactQuery} onChange={(e) => setContactQuery(e.target.value)} placeholder="ابحثي بالاسم..." data-testid="contact-search" />
                    <div className="max-h-80 overflow-y-auto space-y-1">
                        {filtered.length === 0 && <div className="text-center text-slate-400 text-sm py-6">لا توجد جهات اتصال متاحة</div>}
                        {filtered.map((c) => {
                            const Icon = ROLE_ICON[c.role] || UserIcon;
                            return (
                                <button
                                    key={c.id}
                                    onClick={() => startWithContact(c)}
                                    data-testid={`contact-${c.id}`}
                                    className="w-full text-right p-2 rounded-lg hover:bg-slate-50 flex items-center gap-3"
                                >
                                    <Avatar url={c.avatar_url} name={c.full_name} size={36} />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-sm">{c.full_name}</div>
                                        <div className="text-xs text-slate-500">
                                            {ROLE_LABEL[c.role]}
                                            {c.class && ` • ${c.class}`}
                                            {c.specialization && ` • ${SPEC_LABEL[c.specialization] || c.specialization}`}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </DialogContent>
            </Dialog>

            <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
                <AlertDialogContent dir="rtl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>حذف الرسالة من محادثتك</AlertDialogTitle>
                        <AlertDialogDescription>
                            سيتم إخفاء الرسالة من واجهتك فقط. تبقى محفوظة لدى الطرف الآخر ولدى الإدارة.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="btn-cancel-del-msg">إلغاء</AlertDialogCancel>
                        <AlertDialogAction onClick={deleteMessage} data-testid="btn-confirm-del-msg" className="bg-[#C8102E] hover:bg-[#9F1239]">حذف</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
